import { createContext, ReactNode } from 'react';
import {
  useId,
  Toaster,
  useToastController,
  ToastTitle,
  ToastFooter,
  Toast,
  ToastIntent,
  Spinner,
  Link,
  Button,
} from '@fluentui/react-components';
import { OpenRegular } from '@fluentui/react-icons';
import { v4 as uuidv4 } from 'uuid';

// `href` is preferred over `onClick` for navigation - keyboard/right-click/a11y all rely on a real anchor.
export type TToastAction = { label: ReactNode; href?: string; onClick?: () => void };
export type TShow = (
  intent: ToastIntent,
  message: string,
  opts?: { timeout?: number; className?: string; action?: TToastAction }
) => void;
export type TShowProgress = (message: string) => () => void;
type TToastContext = {
  show: TShow;
  showProgress: TShowProgress;
};

// @ts-ignore
export const ToastContext = createContext<TToastContext>(null);

type Props = {
  children: ReactNode;
};

type TStaticFunctions = {
  show: TShow;
};
export const staticFunctions: TStaticFunctions = {
  show: (intent, message) => undefined,
};

export const ToastProvider = ({ children }: Props) => {
  const toasterId = useId('toaster');
  const { dispatchToast, dismissToast } = useToastController(toasterId);

  const show: TShow = (intent, message, opts = {}) => {
    dispatchToast(
      <Toast className={opts.className}>
        <ToastTitle>{message}</ToastTitle>
        {opts.action && (
          <ToastFooter>
            {opts.action.href ? (
              <Button
                as="a"
                href={opts.action.href}
                target="_blank"
                rel="noopener noreferrer"
                appearance="transparent"
                size="small"
                icon={<OpenRegular />}
                iconPosition="after"
                className="!min-w-0 !px-0"
              >
                {opts.action.label}
              </Button>
            ) : (
              <Link onClick={opts.action.onClick}>{opts.action.label}</Link>
            )}
          </ToastFooter>
        )}
      </Toast>,
      {
        intent,
        timeout: opts.timeout ?? (opts.action ? 8000 : ['error', 'info'].includes(intent) ? 4000 : 3000),
      }
    );
  };
  staticFunctions.show = show;

  const showProgress: TShowProgress = (message) => {
    const id = uuidv4();
    dispatchToast(
      <Toast>
        <ToastTitle media={<Spinner size="tiny" />}>{message}</ToastTitle>
      </Toast>,
      { timeout: -1, toastId: id }
    );
    const hideProgress = () => dismissToast(id);
    return hideProgress;
  };

  return (
    <ToastContext.Provider value={{ show, showProgress }}>
      <Toaster toasterId={toasterId} position="top" />
      {children}
    </ToastContext.Provider>
  );
};
